import { useTranslation } from 'react-i18next';
import { SEOHead } from './SEOHead';
import { NavigationLink } from './Router';

export function UltimateGuide() {
  const { i18n } = useTranslation();
  const isNL = i18n.language === 'nl';
  const isFR = i18n.language === 'fr';

  const content = {
    en: {
      title: "The Ultimate Guide to Reporting Food Poisoning in Belgium",
      subtitle: "Everything you need to know about reporting food poisoning to authorities and protecting others",
      lastUpdated: "Last updated: January 2025",
      intro: "If you've experienced food poisoning from a restaurant, takeaway, or food establishment in Belgium, this comprehensive guide will walk you through every step of the reporting process. Learn how to report to the Federal Agency for the Safety of the Food Chain (FASFC/FAVV/AFSCA), what information you'll need, and what happens after you file a report.",
      sections: {
        when: {
          title: "When Should You Report Food Poisoning?",
          content: [
            "You became ill after eating at a restaurant, café, takeaway, or any food establishment",
            "Multiple people who ate the same food became ill",
            "You suspect poor hygiene practices at the establishment",
            "The illness required medical treatment or hospitalization",
            "You want to prevent others from getting sick"
          ]
        },
        how: {
          title: "How to Report Food Poisoning in Belgium",
          steps: [
            {
              title: "1. Gather Information",
              content: "Before reporting, collect: restaurant name and address, date and time of your visit, what you ate and drank, when symptoms started, nature of symptoms, any medical treatment received, receipts or proof of purchase if available"
            },
            {
              title: "2. Report to FASFC/FAVV/AFSCA",
              content: "The Federal Agency for the Safety of the Food Chain is the official authority. You can report via:",
              options: [
                "Online form: meldpunt.belgie.be",
                "Phone: 0800 13 550 (free number)",
                "Email: meldpunt@favv.be",
                "By mail to your local FASFC control unit"
              ]
            },
            {
              title: "3. Report on Our Platform",
              content: "While waiting for official action, warn others immediately by reporting on our platform. This provides real-time alerts to other diners while official processes can take weeks."
            },
            {
              title: "4. Seek Medical Attention",
              content: "If symptoms are severe (high fever, blood in stool, dehydration, symptoms lasting over 3 days), seek immediate medical care. Ask your doctor for documentation which can support your report."
            }
          ]
        },
        what: {
          title: "What Information Do You Need?",
          items: [
            "Restaurant Details: Name, full address, date and time of visit",
            "Food Information: Everything you ate and drank, how it was prepared",
            "Symptoms: When they started, what symptoms, duration, severity",
            "Medical: Any treatment received, doctor's notes, hospital records",
            "Evidence: Receipts, photos of food, witness information",
            "Personal: Your contact details (kept confidential by authorities)"
          ]
        },
        after: {
          title: "What Happens After Reporting?",
          timeline: [
            "Acknowledgment: You'll receive confirmation of your report within 2-3 business days",
            "Investigation: FASFC inspectors may visit the establishment",
            "Testing: Food samples and surfaces may be tested",
            "Action: Depending on findings, actions range from warnings to closure",
            "Follow-up: You may be contacted for additional information",
            "Resolution: The process typically takes 2-6 weeks"
          ]
        },
        rights: {
          title: "Your Rights as a Consumer",
          points: [
            "You have the right to safe food under Belgian and EU law",
            "You can report anonymously on our platform (not to FASFC)",
            "You may be entitled to compensation for medical costs",
            "Establishments cannot retaliate against you for reporting",
            "You can request updates on your complaint from FASFC"
          ]
        },
        prevention: {
          title: "How to Prevent Food Poisoning",
          tips: [
            "Check restaurant hygiene ratings before dining",
            "Look for cleanliness in visible areas",
            "Ensure hot food is served hot, cold food is served cold",
            "Be cautious with raw or undercooked foods",
            "Trust your instincts - if something seems off, don't eat it",
            "Report any concerns immediately"
          ]
        }
      },
      disclaimer: "This guide is for informational purposes only. Always consult medical professionals for health concerns and official authorities for legal matters.",
      cta: {
        report: "Report Food Poisoning Now",
        check: "Check Restaurant Hygiene Scores"
      }
    },
    nl: {
      title: "De Ultieme Gids voor het Melden van Voedselvergiftiging in België",
      subtitle: "Alles wat u moet weten over het melden van voedselvergiftiging aan de autoriteiten en het beschermen van anderen",
      lastUpdated: "Laatst bijgewerkt: januari 2025",
      intro: "Als u voedselvergiftiging heeft opgelopen door een restaurant, afhaalrestaurant of voedselinstelling in België, zal deze uitgebreide gids u door elke stap van het meldingsproces leiden. Leer hoe u melding maakt bij het Federaal Agentschap voor de Veiligheid van de Voedselketen (FAVV), welke informatie u nodig heeft en wat er gebeurt nadat u een melding heeft ingediend.",
      sections: {
        when: {
          title: "Wanneer Moet U Voedselvergiftiging Melden?",
          content: [
            "U werd ziek na het eten in een restaurant, café, afhaalrestaurant of andere voedselinstelling",
            "Meerdere mensen die hetzelfde voedsel aten werden ziek",
            "U vermoedt slechte hygiënepraktijken bij de instelling",
            "De ziekte vereiste medische behandeling of ziekenhuisopname",
            "U wilt voorkomen dat anderen ziek worden"
          ]
        },
        how: {
          title: "Hoe Voedselvergiftiging Melden in België",
          steps: [
            {
              title: "1. Verzamel Informatie",
              content: "Verzamel voor het melden: restaurantnaam en adres, datum en tijd van uw bezoek, wat u at en dronk, wanneer de symptomen begonnen, aard van de symptomen, eventuele medische behandeling, bonnetjes of aankoopbewijs indien beschikbaar"
            },
            {
              title: "2. Meld bij FAVV",
              content: "Het Federaal Agentschap voor de Veiligheid van de Voedselketen is de officiële autoriteit. U kunt melden via:",
              options: [
                "Online formulier: meldpunt.belgie.be",
                "Telefoon: 0800 13 550 (gratis nummer)",
                "E-mail: meldpunt@favv.be",
                "Per post naar uw lokale FAVV-controle-eenheid"
              ]
            },
            {
              title: "3. Meld op Ons Platform",
              content: "Terwijl u wacht op officiële actie, waarschuw anderen onmiddellijk door te melden op ons platform. Dit biedt realtime waarschuwingen aan andere gasten terwijl officiële processen weken kunnen duren."
            },
            {
              title: "4. Zoek Medische Hulp",
              content: "Als de symptomen ernstig zijn (hoge koorts, bloed in ontlasting, uitdroging, symptomen die langer dan 3 dagen duren), zoek dan onmiddellijk medische hulp. Vraag uw arts om documentatie die uw melding kan ondersteunen."
            }
          ]
        },
        what: {
          title: "Welke Informatie Heeft U Nodig?",
          items: [
            "Restaurantgegevens: Naam, volledig adres, datum en tijd van bezoek",
            "Voedselinformatie: Alles wat u at en dronk, hoe het werd bereid",
            "Symptomen: Wanneer ze begonnen, welke symptomen, duur, ernst",
            "Medisch: Eventuele behandeling, doktersnotities, ziekenhuisverslagen",
            "Bewijs: Bonnetjes, foto's van voedsel, getuigeninformatie",
            "Persoonlijk: Uw contactgegevens (vertrouwelijk gehouden door autoriteiten)"
          ]
        },
        after: {
          title: "Wat Gebeurt Er Na de Melding?",
          timeline: [
            "Bevestiging: U ontvangt binnen 2-3 werkdagen een bevestiging van uw melding",
            "Onderzoek: FAVV-inspecteurs kunnen de instelling bezoeken",
            "Testen: Voedselmonsters en oppervlakken kunnen worden getest",
            "Actie: Afhankelijk van de bevindingen variëren acties van waarschuwingen tot sluiting",
            "Follow-up: U kunt worden gecontacteerd voor aanvullende informatie",
            "Afhandeling: Het proces duurt meestal 2-6 weken"
          ]
        },
        rights: {
          title: "Uw Rechten als Consument",
          points: [
            "U heeft recht op veilig voedsel volgens Belgische en EU-wetgeving",
            "U kunt anoniem melden op ons platform (niet bij FAVV)",
            "U kunt recht hebben op vergoeding voor medische kosten",
            "Instellingen kunnen geen represailles tegen u nemen voor het melden",
            "U kunt updates over uw klacht opvragen bij FAVV"
          ]
        },
        prevention: {
          title: "Hoe Voedselvergiftiging Voorkomen",
          tips: [
            "Controleer restaurant hygiënebeoordelingen voor het dineren",
            "Let op netheid in zichtbare gebieden",
            "Zorg ervoor dat warm eten warm wordt geserveerd, koud eten koud",
            "Wees voorzichtig met rauwe of onvoldoende verhitte voedingsmiddelen",
            "Vertrouw op uw instinct - als iets niet goed lijkt, eet het dan niet",
            "Meld eventuele zorgen onmiddellijk"
          ]
        }
      },
      disclaimer: "Deze gids is alleen voor informatieve doeleinden. Raadpleeg altijd medische professionals voor gezondheidsproblemen en officiële autoriteiten voor juridische zaken.",
      cta: {
        report: "Meld Voedselvergiftiging Nu",
        check: "Controleer Restaurant Hygiëne Scores"
      }
    },
    fr: {
      title: "Le Guide Ultime pour Signaler une Intoxication Alimentaire en Belgique",
      subtitle: "Tout ce que vous devez savoir pour signaler une intoxication alimentaire aux autorités et protéger les autres",
      lastUpdated: "Dernière mise à jour: janvier 2025",
      intro: "Si vous avez subi une intoxication alimentaire dans un restaurant, un service de plats à emporter ou un établissement alimentaire en Belgique, ce guide complet vous guidera à travers chaque étape du processus de signalement. Apprenez comment signaler à l'Agence Fédérale pour la Sécurité de la Chaîne Alimentaire (AFSCA), quelles informations vous aurez besoin et ce qui se passe après avoir déposé un rapport.",
      sections: {
        when: {
          title: "Quand Devez-Vous Signaler une Intoxication Alimentaire?",
          content: [
            "Vous êtes tombé malade après avoir mangé dans un restaurant, café, plats à emporter ou tout établissement alimentaire",
            "Plusieurs personnes qui ont mangé la même nourriture sont tombées malades",
            "Vous soupçonnez de mauvaises pratiques d'hygiène dans l'établissement",
            "La maladie a nécessité un traitement médical ou une hospitalisation",
            "Vous voulez empêcher d'autres personnes de tomber malades"
          ]
        },
        how: {
          title: "Comment Signaler une Intoxication Alimentaire en Belgique",
          steps: [
            {
              title: "1. Rassemblez les Informations",
              content: "Avant de signaler, collectez: nom et adresse du restaurant, date et heure de votre visite, ce que vous avez mangé et bu, quand les symptômes ont commencé, nature des symptômes, tout traitement médical reçu, reçus ou preuve d'achat si disponible"
            },
            {
              title: "2. Signalez à l'AFSCA",
              content: "L'Agence Fédérale pour la Sécurité de la Chaîne Alimentaire est l'autorité officielle. Vous pouvez signaler via:",
              options: [
                "Formulaire en ligne: pointdecontact.belgique.be",
                "Téléphone: 0800 13 550 (numéro gratuit)",
                "Email: pointdecontact@afsca.be",
                "Par courrier à votre unité de contrôle AFSCA locale"
              ]
            },
            {
              title: "3. Signalez sur Notre Plateforme",
              content: "En attendant une action officielle, avertissez immédiatement les autres en signalant sur notre plateforme. Cela fournit des alertes en temps réel aux autres convives alors que les processus officiels peuvent prendre des semaines."
            },
            {
              title: "4. Consultez un Médecin",
              content: "Si les symptômes sont graves (forte fièvre, sang dans les selles, déshydratation, symptômes durant plus de 3 jours), consultez immédiatement un médecin. Demandez à votre médecin une documentation qui peut soutenir votre rapport."
            }
          ]
        },
        what: {
          title: "Quelles Informations Avez-Vous Besoin?",
          items: [
            "Détails du Restaurant: Nom, adresse complète, date et heure de visite",
            "Informations Alimentaires: Tout ce que vous avez mangé et bu, comment c'était préparé",
            "Symptômes: Quand ils ont commencé, quels symptômes, durée, gravité",
            "Médical: Tout traitement reçu, notes du médecin, dossiers hospitaliers",
            "Preuves: Reçus, photos de nourriture, informations des témoins",
            "Personnel: Vos coordonnées (gardées confidentielles par les autorités)"
          ]
        },
        after: {
          title: "Que Se Passe-t-il Après le Signalement?",
          timeline: [
            "Accusé de réception: Vous recevrez une confirmation de votre rapport dans les 2-3 jours ouvrables",
            "Enquête: Les inspecteurs de l'AFSCA peuvent visiter l'établissement",
            "Tests: Des échantillons alimentaires et des surfaces peuvent être testés",
            "Action: Selon les conclusions, les actions vont des avertissements à la fermeture",
            "Suivi: Vous pouvez être contacté pour des informations supplémentaires",
            "Résolution: Le processus prend généralement 2-6 semaines"
          ]
        },
        rights: {
          title: "Vos Droits en tant que Consommateur",
          points: [
            "Vous avez droit à une alimentation sûre selon la loi belge et européenne",
            "Vous pouvez signaler anonymement sur notre plateforme (pas à l'AFSCA)",
            "Vous pourriez avoir droit à une compensation pour les frais médicaux",
            "Les établissements ne peuvent pas exercer de représailles contre vous pour avoir signalé",
            "Vous pouvez demander des mises à jour sur votre plainte à l'AFSCA"
          ]
        },
        prevention: {
          title: "Comment Prévenir une Intoxication Alimentaire",
          tips: [
            "Vérifiez les notes d'hygiène du restaurant avant de dîner",
            "Recherchez la propreté dans les zones visibles",
            "Assurez-vous que les aliments chauds sont servis chauds, les aliments froids sont servis froids",
            "Soyez prudent avec les aliments crus ou insuffisamment cuits",
            "Faites confiance à votre instinct - si quelque chose semble anormal, ne le mangez pas",
            "Signalez immédiatement toute préoccupation"
          ]
        }
      },
      disclaimer: "Ce guide est à titre informatif uniquement. Consultez toujours des professionnels de la santé pour les problèmes de santé et les autorités officielles pour les questions juridiques.",
      cta: {
        report: "Signaler une Intoxication Alimentaire Maintenant",
        check: "Vérifier les Scores d'Hygiène des Restaurants"
      }
    }
  };

  const lang = isFR ? 'fr' : (isNL ? 'nl' : 'en');
  const t = content[lang];

  return (
    <>
      <SEOHead page="ultimate-guide" />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto px-4 py-8 md:px-8">
          {/* Header */}
          <header className="mb-12 text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-4">
              {t.title}
            </h1>
            <p className="text-xl text-gray-600 dark:text-gray-300 mb-2">
              {t.subtitle}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {t.lastUpdated}
            </p>
          </header>

          {/* Introduction */}
          <div className="mb-12 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <p className="text-lg text-gray-700 dark:text-gray-300">
              {t.intro}
            </p>
          </div>

          {/* Table of Contents */}
          <nav className="mb-12 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg">
            <h2 className="text-2xl font-semibold mb-4 text-gray-900 dark:text-white">
              {lang === 'fr' ? 'Table des Matières' : (lang === 'nl' ? 'Inhoudsopgave' : 'Table of Contents')}
            </h2>
            <ul className="space-y-2">
              {Object.entries(t.sections).map(([key, section]) => (
                <li key={key}>
                  <a 
                    href={`#${key}`}
                    className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    {section.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {/* Main Content Sections */}
          <article className="space-y-12">
            {/* When to Report */}
            <section id="when" className="scroll-mt-20">
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.when.title}
              </h2>
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-6 rounded-lg">
                <ul className="space-y-3">
                  {t.sections.when.content.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-yellow-600 dark:text-yellow-400 mr-3">✓</span>
                      <span className="text-gray-700 dark:text-gray-300">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* How to Report */}
            <section id="how" className="scroll-mt-20">
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.how.title}
              </h2>
              <div className="space-y-6">
                {t.sections.how.steps.map((step, index) => (
                  <div key={index} className="bg-gray-50 dark:bg-gray-800 p-6 rounded-lg">
                    <h3 className="text-xl font-semibold mb-3 text-gray-900 dark:text-white">
                      {step.title}
                    </h3>
                    <p className="text-gray-700 dark:text-gray-300 mb-3">
                      {step.content}
                    </p>
                    {step.options && (
                      <ul className="list-disc list-inside space-y-2 ml-4">
                        {step.options.map((option, idx) => (
                          <li key={idx} className="text-gray-600 dark:text-gray-400">
                            {option}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </section>

            {/* What Information */}
            <section id="what" className="scroll-mt-20">
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.what.title}
              </h2>
              <div className="bg-blue-50 dark:bg-blue-900/20 p-6 rounded-lg">
                <ul className="space-y-4">
                  {t.sections.what.items.map((item, index) => (
                    <li key={index} className="text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">{item.split(':')[0]}:</span>
                      {item.split(':')[1]}
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* After Reporting */}
            <section id="after" className="scroll-mt-20">
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.after.title}
              </h2>
              <div className="space-y-4">
                {t.sections.after.timeline.map((item, index) => (
                  <div key={index} className="flex items-start space-x-4">
                    <div className="flex-shrink-0 w-8 h-8 bg-blue-500 text-white rounded-full flex items-center justify-center text-sm font-semibold">
                      {index + 1}
                    </div>
                    <p className="text-gray-700 dark:text-gray-300">
                      <span className="font-semibold">{item.split(':')[0]}:</span>
                      {item.split(':')[1]}
                    </p>
                  </div>
                ))}
              </div>
            </section>

            {/* Consumer Rights */}
            <section id="rights" className="scroll-mt-20">
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.rights.title}
              </h2>
              <div className="bg-green-50 dark:bg-green-900/20 p-6 rounded-lg">
                <ul className="space-y-3">
                  {t.sections.rights.points.map((point, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-green-600 dark:text-green-400 mr-3">⚖️</span>
                      <span className="text-gray-700 dark:text-gray-300">{point}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Prevention */}
            <section id="prevention" className="scroll-mt-20">
              <h2 className="text-3xl font-semibold mb-6 text-gray-900 dark:text-white">
                {t.sections.prevention.title}
              </h2>
              <div className="bg-purple-50 dark:bg-purple-900/20 p-6 rounded-lg">
                <ul className="space-y-3">
                  {t.sections.prevention.tips.map((tip, index) => (
                    <li key={index} className="flex items-start">
                      <span className="text-purple-600 dark:text-purple-400 mr-3">💡</span>
                      <span className="text-gray-700 dark:text-gray-300">{tip}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          </article>

          {/* Disclaimer */}
          <div className="mt-12 p-6 bg-red-50 dark:bg-red-900/20 rounded-lg">
            <p className="text-sm text-gray-600 dark:text-gray-400 italic">
              ⚠️ {t.disclaimer}
            </p>
          </div>

          {/* Call to Action */}
          <div className="mt-12 text-center space-y-4">
            <NavigationLink
              to="/report-food-poisoning"
              className="inline-block px-8 py-4 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 transition-colors"
            >
              {t.cta.report}
            </NavigationLink>
            <div>
              <NavigationLink
                to="/"
                className="text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 underline"
              >
                {t.cta.check}
              </NavigationLink>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}